<script setup>
import { computed, onMounted, watch, ref, onBeforeUnmount } from "vue";
import Highcharts from "@/plugins/highcharts-bundle";
import { useChartDataStore } from "@/stores/chart-data";

const props = defineProps({
    symbol: {
        type: String,
        required: true,
    },

    chartInterval: {
        type: Number,
        default: 15,
        validator: value => [1, 5, 15, 30, 60].includes(value),
    },

    showNoDataMessage: {
        type: Boolean,
        default: true,
    },

    marketSession: {
        type: String,
        default: "all",
        validator: value => ["mkt", "all", "premkt", "postmkt"].includes(value),
    },
});

const chartDataStore = useChartDataStore();

const chartRef = ref(null);
const chartInstance = ref(null);
const resizeObserver = ref(null);
const chartId = ref(
    `chart-${props.symbol.replace(/[^a-zA-Z0-9]/g, "-")}-${Math.floor(Math.random() * 10000)}`
);

const chartParams = computed(() => ({
    interval: props.chartInterval,
    marketSession: props.marketSession || "all",
}));

const chartData = computed(() => {
    const data = chartDataStore.getFormattedChartData(props.symbol, chartParams.value);
    return data?.series?.[0]?.data || [];
});

/**
 * Opening price of the chart data (first data point in the session)
 */
const openingPrice = computed(() => {
    // Get the formatted chart data that now includes the opening price
    const data = chartDataStore.getFormattedChartData(props.symbol, chartParams.value);

    // Use the session's opening price from the data store (not the first point's close)
    if (data && data.openingPrice !== undefined && data.openingPrice !== null) {
        return data.openingPrice;
    }

    // Fallback to first point's close if needed (should not happen with updated code)
    if (chartData.value && chartData.value.length > 0) {
        return chartData.value[0][1];
    }

    return null;
});

const hasValidChartData = computed(() => {
    return chartData.value?.length; // Need at least 2 points for a line
});

const isShowingDefaultData = computed(() => !hasValidChartData.value);

const updateChart = () => {
    if (!chartInstance.value) return;

    try {
        if (!hasValidChartData.value) {
            console.warn(`No valid data for ${props.symbol}`);
            return;
        }

        chartInstance.value.yAxis[0].update(
            {
                plotLines:
                    openingPrice.value !== null
                        ? [
                              {
                                  value: openingPrice.value,
                                  className: "opening-price-line",
                                  width: 1,
                                  zIndex: 1,
                              },
                          ]
                        : [],
            },
            false
        );

        chartInstance.value.series[0].update(
            {
                data: chartData.value || [],
                zones:
                    openingPrice.value !== null
                        ? [
                              {
                                  value: openingPrice.value,
                                  className: "negative-zone",
                              },
                              {
                                  className: "positive-zone",
                              },
                          ]
                        : undefined,
            },
            true
        );
    } catch (error) {
        console.error(`Error updating chart for ${props.symbol}:`, error);
    }
};

onMounted(async () => {
    if (!chartRef.value) return;

    if (!Highcharts || typeof Highcharts.chart !== "function") {
        console.error("Highcharts is not properly initialized");
        return;
    }

    try {
        if (!Highcharts) {
            console.error("Highcharts is not loaded");
            return;
        }

        if (!Highcharts.chart) {
            console.error("Highcharts.chart function not available");
            return;
        }

        chartInstance.value = Highcharts.chart(chartId.value, {
            chart: {
                type: "line",
                margin: [0, 0, 0, 0],
                spacing: [0, 0, 0, 0],
                backgroundColor: "transparent",
                reflow: true,
                className: "qmodii-minichart",
            },
            title: { text: null },
            xAxis: { visible: false, labels: { enabled: false } },
            yAxis: {
                plotLines: openingPrice.value
                    ? [
                          {
                              value: openingPrice.value,
                              className: "opening-price-line",
                              width: 1,
                              zIndex: 1,
                          },
                      ]
                    : [],
                lineWidth: 0,
                gridLineWidth: 0,
                labels: { enabled: false },
                title: { text: null },
            },
            tooltip: { enabled: false },
            credits: { enabled: false },
            legend: { enabled: false },
            plotOptions: {
                series: {
                    animation: false,
                    lineWidth: 1,
                    states: { hover: { enabled: false } },
                    marker: { enabled: false },
                    zones:
                        openingPrice.value !== null
                            ? [
                                  {
                                      value: openingPrice.value,
                                      className: "negative-zone",
                                  },
                                  {
                                      className: "positive-zone",
                                  },
                              ]
                            : undefined,
                },
            },
            series: [
                {
                    name: props.symbol,
                    data: hasValidChartData.value ? chartData.value : [1, 1],
                    className: isShowingDefaultData.value ? "chart-series no-data-line" : "chart-series",
                },
            ],
            noData: {
                enabled: false,
            }
        });

        resizeObserver.value = new ResizeObserver(entries => {
            if (chartInstance.value) {
                setTimeout(() => {
                    chartInstance.value.reflow();
                }, 10);
            }
        });

        resizeObserver.value.observe(chartRef.value);
    } catch (error) {
        console.error(`Error creating chart for ${props.symbol}:`, error);
    }
});

watch(
    [chartData, openingPrice],
    () => {
        updateChart();
    },
    { deep: true }
);

onBeforeUnmount(() => {
    if (resizeObserver.value) {
        resizeObserver.value.disconnect();
        resizeObserver.value = null;
    }

    if (chartInstance.value) {
        chartInstance.value.destroy();
        chartInstance.value = null;
    }
});
</script>

<template>
    <div :id="chartId" ref="chartRef" class="qmodii-minichart-container"></div>
</template>

<style>
.qmodii-main {
    --qmodii-minichart-opening-price-color: #707070;
    --qmodii-minichart-positive-color: var(--qmodii-up-color);
    --qmodii-minichart-negative-color: var(--qmodii-down-color);
    --qmodii-minichart-height: 60px;
}
</style>

<style scoped>
.qmodii-minichart-container {
    width: 100%;
    height: var(--qmodii-minichart-height);
    position: relative;
    overflow: hidden;
}

:deep(.highcharts-container) {
    position: absolute !important;
    width: 100% !important;
    height: 100% !important;
    top: 0;
    left: 0;
}

:deep(svg.highcharts-root) {
    width: 100% !important;
    height: 100% !important;
}

:deep(.highcharts-background) {
    width: 100% !important;
    height: 100% !important;
}

:deep(.qmodii-minichart .opening-price-line) {
    stroke: var(--qmodii-minichart-opening-price-color) !important;
}

:deep(.qmodii-minichart .negative-zone) {
    fill: none !important;
    stroke: var(--qmodii-minichart-negative-color) !important;
}

:deep(.qmodii-minichart .positive-zone) {
    fill: none !important;
    stroke: var(--qmodii-minichart-positive-color) !important;
}

:deep(.qmodii-minichart .chart-series path) {
    stroke-width: 1px;
}

:deep(.qmodii-minichart .no-data-line) {
    path {
        stroke: var(--qmodii-minichart-opening-price-color) !important;
    }
}
</style>
